/**
 * BOM Form Page
 *
 * Wrapper around BomForm component for the main page route
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import bomService from '../services/bomService';
import BomForm from '../components/BomForm';

function BomFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // Load BOM for editing
  const loadBom = useCallback(async () => {
    if (!isEditMode) return;

    try {
      setLoading(true);
      setError(null);

      const bom = await bomService.getBom(id);
      setInitialData(bom);
    } catch (err) {
      console.error('Failed to load BOM:', err);
      setError('Failed to load BOM. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    loadBom();
  }, [loadBom]);

  const handleBack = () => {
    if (isEditMode) {
      navigate(`/bom/${id}`);
    } else {
      navigate('/bom');
    }
  };

  const handleSuccess = (result) => {
    navigate(`/bom/${result._id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditMode ? '编辑BOM' : '新建BOM'}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode
                ? '更新物料清单详情'
                : '为成品定义新的物料清单'}
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <BomForm 
          initialData={initialData} 
          onSuccess={handleSuccess}
        />
      </div>
    </Layout>
  );
}

export default BomFormPage;
